import auth from "../../../utils/authentication";
import {Helmet} from "react-helmet";
import icon from "../../../images/pageIcon.png";
import React, {useEffect} from "react";
import AppExtraOptions from "./appExtraOptions/app-extra-options";
import {FullWindowSpinner, TitlePage} from "../../shared";
import {getCachedImageUrl} from "../../../utils/utils";

import '../../chatbot/chatbotHome/chatbotHome.css';
import './myProfileView.scss';
import {Navigate, Route, Routes, useLocation} from "react-router-dom";
import useUserPreferences from "../../user-app-config/hooks/use-user-preferences";
import {getTmdbImage} from "../../../utils/consts";
import {useIntl} from "react-intl";
import MyProfilePosts from "./my-profile-subroutes/my-profile-posts/my-profile-posts";
import MyProfilePrivate from "./my-profile-subroutes/my-profile-private/my-profile-private";
import MyProfileLists from "./my-profile-subroutes/my-profile-lists/my-profile-lists";
import MyProfileCollectionDetails
    from "./my-profile-subroutes/my-profile-lists/my-profile-collection-details/my-profile-collection-details";
import MyProfileReviews from "./my-profile-subroutes/my-profile-reviews/my-profile-reviews";
import {MyProfile} from "../../profile";
import SquadflixDevicesPage from "../../cap17drive/squadflix-devices-page/squadflix-devices-page";
import HeroLayout from "../../shared/hero-layout/hero-layout";
import {toast} from "react-toastify";
import ButtonV2 from "../../shared/button-v2/button-v2";
import {profileSubroutes} from "./profileSubroutes";
import {MyGroups} from "../../group";
import MyProfileFriends from "./my-profile-subroutes/my-profile-friends/MyProfileFriends";
import {SquadflixFriendsApi} from "../../../apis/squadflix-friends/SquadflixFriendsApi";

const MyProfileView = () => {
    const user = auth.currentUser;
    const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
    const location = useLocation();
    const {userPreferences, customClaims} = useUserPreferences();
    const [pendingRequestsCount, setPendingRequestsCount] = React.useState(0);

    const intl = useIntl();

    const fetchPendingRequests = React.useCallback(async () => {
        try {
            const res = await SquadflixFriendsApi.listFriendRequests();
            if (res && res.success && res.received) {
                setPendingRequestsCount(res.received.length);
            }
        } catch (err) {
            console.error("Error fetching friend requests count:", err);
        }
    }, []);

    useEffect(() => {
        if (user) {
            fetchPendingRequests();
        }
    }, [user, fetchPendingRequests]);

    const dynamicSubroutes = React.useMemo(() => {
        return [
            {
                icon: <i className="fa-solid fa-inbox"></i>,
                text: "Publicaciones",
                route: "/my-profile/posts",
            },
            {
                icon: (
                    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        <i className="fa-solid fa-users"></i>
                        {pendingRequestsCount > 0 && (
                            <span style={{
                                position: 'absolute',
                                top: '-4px',
                                right: '-4px',
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                backgroundColor: '#dc3545',
                                border: '1.5px solid var(--border-color, #23272b)',
                            }} />
                        )}
                    </div>
                ),
                text: "Amigos",
                route: "/my-profile/friends",
            },
            {
                icon: <i className="fa-solid fa-bookmark"></i>,
                text: "Guardados",
                route: "/my-profile/collections",
            },
            {
                icon: <i className="fa-solid fa-star"></i>,
                text: "Reseñas",
                route: "/my-profile/reviews",
            }
        ];
    }, [pendingRequestsCount]);

    function openSidebar() {
        setIsSidebarOpen(!isSidebarOpen);
    }

    useEffect(() => {
        setIsSidebarOpen(false);
    }, [location]);

    const copyUserUrl = () => {
        let userUrl = window.location.origin + `/user/${user.displayName}/${customClaims?.userCode || ''}`;
        const path = window.location.pathname.split('/my-profile')[1];
        if (path) {
            userUrl += path;
        }
        navigator.clipboard.writeText(userUrl).then(() => {
            toast.info("URL de usuario copiada al portapapeles");
        });
    };

    useEffect(() => {
        // Scroll to top when location changes
        window.scrollTo(0, 0);
    }, [location]);

    if (!user) {
        return <FullWindowSpinner/>
    }

    return (<>
        <Helmet>
            <meta charSet="utf-8"/>
            <title>SquadDisk | {intl.formatMessage({id: "app.my-profile"})}</title>
            <link rel="icon" href={icon}/>
        </Helmet>
        <HeroLayout
            title={
                <>{user.displayName}
                    <button className="user-profile-details__copy-user-url" onClick={copyUserUrl}><i
                        className="fa-solid fa-link text-muted fa-xs"></i></button>
                </>}
            subtitle={"#" + customClaims?.userCode}
            options={
                <ButtonV2 className="hero-layout__subroutes-btn" onClick={openSidebar}>
                    <i className="fa-solid fa-ellipsis"></i>
                </ButtonV2>
            }
            imgStyleOverrides={{
                aspectRatio: '1 / 1',
                objectFit: 'cover',
            }}
            imgUrl={getCachedImageUrl(user.photoURL)}
            backgroundUrl={userPreferences.profileBackground ? getTmdbImage(userPreferences.profileBackground) : undefined}
            routes={dynamicSubroutes}>
            <Routes>
                <Route path="/posts" element={<MyProfilePosts/>}/>
                <Route path="/private" element={<MyProfilePrivate/>}/>
                <Route path="/friends" element={<MyProfileFriends onRequestsChange={fetchPendingRequests}/>}/>
                <Route path="/collections" element={<MyProfileLists/>}/>
                <Route path="/collections/:id" element={<MyProfileCollectionDetails/>}/>
                <Route path="/reviews" element={<MyProfileReviews uid={user.uid}/>}/>
                <Route path="/config" element={<MyProfile/>}/>
                <Route path="/devices" element={<SquadflixDevicesPage/>}/>
                <Route path="/groups" element={<MyGroups/>}></Route>
                <Route path="/*" element={<Navigate to={dynamicSubroutes[0].route}/>}/>
            </Routes>
        </HeroLayout>
        <>
            <div className={`chatbot-home__sidebar-overlay ${isSidebarOpen ? 'open' : ''}`}
                 onClick={openSidebar}>
            </div>
            <div className={`chatbot-home__sidebar ${isSidebarOpen ? 'open' : ''}`} style={{
                paddingBottom: '70px',
            }}>
                <TitlePage icon="fa-solid fa-link" className="mt-2" title="Links"
                           rightClass="gap-3"
                           rightOptions={
                               <div onClick={openSidebar} className="chatbot-header__sidebar-btn">
                                   <i className="fa-solid fa-times fa-lg"></i>
                               </div>
                           } titleClass={undefined} leftClass={undefined} disableScroll={undefined}
                           hideSeparator={undefined} fixedHeight={undefined} style={undefined}/>
                <AppExtraOptions hideTitle userPreferences={userPreferences}/>
            </div>
        </>
    </>);
}

export default MyProfileView;
